'use client';

import { useEffect } from 'react';

/**
 * Self-heal for stale-deploy chunk failures.
 *
 * The PWA caches the app shell (HTML) aggressively for offline use. After we
 * ship a new deploy, content-addressed Next.js chunks get new hashes and the
 * OLD hashes 404 on the production domain. An iPad still running the previously
 * cached HTML then fails to lazy-load a `dynamic()` feature — the recital sheet
 * viewer (SIScoreReader) and the show-script reader are both dynamic chunks —
 * so "sheet music won't open" / "can't find the show script" while the cached
 * shell itself still renders. The SI kiosk deliberately suppresses the
 * "Reload" toast (a child would tap it into a dead offline page), so nothing
 * was recovering from this automatically.
 *
 * This listener watches for a chunk / dynamic-import load failure and, IF the
 * device is online, reloads once to pull fresh HTML (the SW serves navigations
 * network-first, so a reload lands on the current deploy whose chunks exist).
 *
 * Loop safety: we record the last reload time in sessionStorage and refuse to
 * reload again within 20s. So a genuine stale-chunk error self-heals on the
 * next interaction, but if the reload itself immediately re-errors (something
 * other than deploy skew), we stop instead of thrashing. Offline → we do
 * nothing (a reload couldn't fetch the chunk anyway).
 */
const LAST_RELOAD_KEY = 'cfva-last-chunk-reload';
const MIN_GAP_MS = 20_000;

function isChunkLoadError(message: string, name?: string): boolean {
  if (name === 'ChunkLoadError') return true;
  return (
    /Loading (?:CSS )?chunk [\w-]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    // Safari / iOS phrasing — the common one on the iPads.
    /Importing a module script failed/i.test(message)
  );
}

function maybeReload() {
  try {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    const last = Number(window.sessionStorage.getItem(LAST_RELOAD_KEY) || 0);
    const now = Date.now();
    if (now - last < MIN_GAP_MS) return; // just tried — don't loop
    window.sessionStorage.setItem(LAST_RELOAD_KEY, String(now));
  } catch {
    // sessionStorage can throw in locked-down/private contexts; reload anyway,
    // but only if we can't even check the guard (rare).
  }
  window.location.reload();
}

export default function ChunkErrorReloader() {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onError = (e: ErrorEvent) => {
      const msg = e.message || e.error?.message || '';
      if (isChunkLoadError(msg, e.error?.name)) maybeReload();
    };
    const onRejection = (e: PromiseRejectionEvent) => {
      const reason = e.reason;
      const msg = typeof reason === 'string' ? reason : reason?.message || '';
      if (isChunkLoadError(msg, reason?.name)) maybeReload();
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  return null;
}
